import { uid } from './utils';

/**
 * Identifiants des enregistrements que le serveur n'a pas encore vus.
 *
 * `useSyncedCollection` lit le genre de l'identifiant pour savoir quoi faire :
 * **un nombre veut dire « le serveur connaît cet enregistrement »**, et la
 * synchronisation envoie alors une mise à jour. Tout le reste est nouveau, et
 * part en création.
 *
 * Plusieurs écrans attribuaient pourtant un nombre calculé de leur côté — le
 * plus grand identifiant connu, plus un. La synchronisation y voyait un
 * enregistrement existant et demandait au serveur de le mettre à jour ; il ne
 * l'avait jamais vu, répondait « introuvable », et la saisie était perdue.
 * Créer un client, un employé ou un article ne marchait tout simplement pas.
 *
 * Le remède tient en une règle : ne jamais fabriquer un identifiant numérique.
 * Le serveur seul les attribue.
 */

/**
 * Identifiant provisoire, le temps que le serveur en attribue un vrai.
 *
 * @returns {string} un identifiant que la synchronisation tiendra pour nouveau
 */
export function idProvisoire() {
  return uid();
}

/**
 * Prochaine référence d'une série, du type {@code MAT-001}.
 *
 * <p>La référence se numérotait sur l'identifiant, ce qui la liait à un nombre
 * que l'écran n'a plus le droit de fabriquer. Elle se numérote désormais sur
 * ce qui existe déjà, ce qu'elle aurait dû faire depuis le début : c'est un
 * numéro d'ordre, non une clé.
 *
 * @param {string} prefixe préfixe de la série, par exemple {@code MAT}
 * @param {object[]} existants enregistrements déjà présents
 * @param {number} [rang] position dans un ajout groupé, à partir de 0
 * @returns {string} la référence à poser
 */
export function prochaineReference(prefixe, existants, rang = 0) {
  const numeros = (existants || [])
    .map((x) => {
      const m = new RegExp('^' + prefixe + '-(\\d+)$').exec(String(x?.ref || ''));
      return m ? +m[1] : 0;
    })
    .filter((n) => n > 0);
  const suivant = (numeros.length ? Math.max(...numeros) : (existants || []).length) + 1 + rang;
  return prefixe + '-' + String(suivant).padStart(3, '0');
}

// Identifiants attribués par le serveur aux enregistrements créés pendant la
// session, indexés par leur identifiant provisoire.
const attribues = new Map();

/**
 * Vrai pour un identifiant fabriqué par l'écran ({@link idProvisoire}).
 *
 * @param {*} valeur valeur d'un champ de référence
 * @returns {boolean} vrai pour une chaîne qui n'est pas un nombre
 */
export function estProvisoire(valeur) {
  return typeof valeur === 'string' && valeur !== '' && !/^\d+$/.test(valeur);
}

/**
 * Retient l'identifiant que le serveur a donné à un enregistrement créé.
 *
 * Un client créé « Depuis mes contacts » dans une offre a d'abord un
 * identifiant provisoire, et l'offre le désigne ainsi. Le serveur, qui attend
 * un nombre, refusait l'offre : elle était perdue, le client restait seul.
 * Les pièces qui désignent ce client trouvent ici son vrai numéro.
 *
 * @param {*} provisoire identifiant donné par l'écran
 * @param {number} definitif identifiant donné par le serveur
 */
export function noteIdAttribue(provisoire, definitif) {
  if (typeof definitif === 'number' && provisoire != null) {
    crees.set(String(provisoire), definitif);
  }
  if (estProvisoire(provisoire) && typeof definitif === 'number') {
    attribues.set(provisoire, definitif);
  }
}

// Toutes les créations de la session, même sous un identifiant local fait de
// chiffres (le numéro d'une offre, « 23 ») : l'écran retrouve la pièce qu'il
// vient d'établir, et non celle d'un collègue qui porte le même numéro.
const crees = new Map();

/**
 * L'identifiant que le serveur a donné à la pièce créée sous cet identifiant
 * local, quel qu'il soit.
 *
 * @param {*} local identifiant donné par l'écran
 * @returns {number|undefined} l'identifiant du serveur, s'il est connu
 */
export function idCreePour(local) {
  return local == null ? undefined : crees.get(String(local));
}

/**
 * L'identifiant du serveur, si l'enregistrement en a reçu un.
 *
 * @param {*} id identifiant connu de l'écran
 * @returns {*} l'identifiant du serveur, sinon celui reçu, inchangé
 */
export function idDefinitif(id) {
  return estProvisoire(id) && attribues.has(id) ? attribues.get(id) : id;
}

/**
 * Remplace, dans une charge utile, les références provisoires par les
 * identifiants que le serveur a attribués entre-temps.
 *
 * Sont des références les champs dont le nom finit par « Id » (`clientId`,
 * `siteId`…). Une référence encore provisoire désigne un enregistrement que
 * le serveur n'a pas encore créé : la charge utile doit attendre.
 *
 * @param {object} dto charge utile à envoyer
 * @returns {{dto: object, enAttente: boolean}} la charge utile résolue, et vrai
 *   si une référence attend encore son identifiant
 */
export function resoutReferences(dto) {
  if (!dto || typeof dto !== 'object') return { dto, enAttente: false };
  let enAttente = false;
  const resolu = { ...dto };
  for (const [cle, valeur] of Object.entries(dto)) {
    if (!/[a-z]Id$/.test(cle) || !estProvisoire(valeur)) continue;
    const definitif = idDefinitif(valeur);
    if (typeof definitif === 'number') resolu[cle] = definitif;
    else enAttente = true;
  }
  return { dto: resolu, enAttente };
}

/** Oublie les identifiants attribués (tests, changement de session). */
export function oublieIdsAttribues() {
  attribues.clear();
}
